import { ApPayee } from '@/components/ApPayee';
import { DeptSelect } from '@/components/DeptSelect';
import { KeywordCont } from '@/components/KeywordCont';
import { OfficeMember } from '@/components/OfficeMember';
import { Amount } from '@/features/approving_invoice_reports/components/Search/components/Amount';
import { DateRangeSelect } from '@/features/approving_invoice_reports/components/Search/components/DateRangeSelect';
import { FormCheckBox } from '@/features/approving_invoice_reports/components/Search/components/FormCheckBox';
import { FormContainer } from '@/features/approving_invoice_reports/components/Search/components/FormContainer';
import { FromInputSearch } from '@/features/approving_invoice_reports/components/Search/components/FromInputSearch';
import { useConfigForm } from '@/features/approving_invoice_reports/components/Search/hooks/useConfigForm';
import { SearchFormData } from '@/features/approving_invoice_reports/components/Search/hooks/useSearchForm';
import { BaseOption } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import { ButtonV2, Form, Label } from '@moneyforward/ap-frontend-components';
import { Flex } from 'antd';
import classnames from 'classnames/bind';
import { UseFormReturn } from 'react-hook-form';
import styles from '../Search.module.scss';
import { InvoiceMFFileStatusesSelect } from './InvoiceMFFileStatusesSelect';
import { InvoiceStatusesSelect } from './InvoiceStatusesSelect';

const cx = classnames.bind(styles);

interface SearchFormProps
  extends Pick<
    ReturnType<typeof useConfigForm>,
    | 'customProps'
    | 'checkLengthValidationRules'
    | 'deptsExisted'
    | 'reportDeptsSetting'
  > {
  form: UseFormReturn<SearchFormData>;
  onSubmit: (data: SearchFormData) => void;
  onReset: () => void;
  deptOptions: BaseOption[];
  memberOptions: BaseOption[];
  appPayeeDefaultOptions: BaseOption[];
  reportDeptDefaultOptions: BaseOption[];
  isLoading?: boolean;
}

export const SearchForm = ({
  form,
  onSubmit,
  onReset,
  deptOptions,
  memberOptions,
  customProps,
  checkLengthValidationRules,
  deptsExisted,
  reportDeptsSetting,
  appPayeeDefaultOptions,
  reportDeptDefaultOptions,
  isLoading = false,
}: SearchFormProps) => {
  const { t } = useTranslation();
  const { control } = form;

  return (
    <Form<SearchFormData> formMethod={form} onSubmit={onSubmit}>
      {/* Row 1 */}
      <Flex gap={8} className={cx('form-row')}>
        {/* Row 1-1 keyword input */}
        <FormContainer className={cx('field-width-fill')}>
          <div>
            <Label
              text={t('business_document_key_word')}
              tooltip={t('business_document_key_word_tooltip')}
            />
            <FromInputSearch
              control={control}
              inputProps={customProps?.inputProps?.query}
              name='query'
              inputId='query'
              rules={checkLengthValidationRules}
              testId='query-input'
            />
          </div>
        </FormContainer>
        {/* Row 1-2 report number input */}
        <FormContainer className={cx('field-width-120')}>
          <KeywordCont
            control={form.control}
            name='number'
            labelProps={customProps?.labelProps?.number}
            inputProps={customProps?.inputProps?.number}
            rules={checkLengthValidationRules}
          />
        </FormContainer>
        {/* Row 1-3 application status input */}
        <FormContainer className={cx('field-width-240')}>
          <div className={cx('select-wrapper')}>
            <label className={cx('label-text')}>
              {t('approving_invoice_reports.table.columns.application_status')}
            </label>
            <InvoiceStatusesSelect control={control} name='status' />
          </div>
        </FormContainer>
        {/* Row 1-4 office member input */}
        <FormContainer className={cx('field-width-240')}>
          <div className={cx('select-wrapper')}>
            <label className={cx('label-text')}>
              {t('applicant_office_member')}
            </label>
            <OfficeMember<SearchFormData>
              control={control}
              name='office_member_ids'
              inputId='applicant'
              selectProps={customProps?.selectProps?.config}
              defaultOptions={memberOptions ?? []}
            />
          </div>
        </FormContainer>
        {/* Row 1-5 payee input */}
        <FormContainer className={cx('field-width-240')}>
          <div className={cx('select-wrapper-app-payee')}>
            <label className={cx('label-text')}>{t('ap_payee')}</label>
            <ApPayee<SearchFormData>
              control={control}
              name='ap_payee_id'
              inputId='payee'
              formContext={undefined}
              defaultOptions={appPayeeDefaultOptions}
              selectProps={customProps?.selectProps?.configApPayee}
            />
          </div>
        </FormContainer>
      </Flex>

      {/* Row 2 */}
      <Flex gap={8} className={cx('form-row')}>
        {/* Row 2-1 applicant department input */}
        {deptsExisted?.enabled && (
          <FormContainer className={cx('field-width-fill')}>
            <label className={cx('label-text')}>
              {t('applicant_department')}
            </label>
            <div className={cx('select-wrapper')}>
              <DeptSelect<SearchFormData>
                mode='multiple'
                control={control}
                name='dept_ids'
                inputId='applicantDepartment'
                selectProps={
                  customProps?.selectProps?.configApplicantDepartment
                }
                defaultOptions={deptOptions ?? []}
              />
            </div>
          </FormContainer>
        )}
        {/* Row 2-2 report department input */}
        {deptsExisted?.enabled && reportDeptsSetting?.enabled && (
          <FormContainer className={cx('field-width-fill')}>
            <label className={cx('label-text')}>
              {t('selected_department')}
            </label>
            <div className={cx('select-wrapper')}>
              <DeptSelect<SearchFormData>
                mode='multiple'
                control={control}
                name='report_dept_ids'
                inputId='selectedDepartment'
                selectProps={customProps?.selectProps?.configDept}
                defaultOptions={reportDeptDefaultOptions}
              />
            </div>
          </FormContainer>
        )}
        {/* Row 2-3 total amount input */}
        <FormContainer className={cx('field-width-hug')}>
          <label className={cx('label-text')}>{t('total_amount')}</label>
          <div className={cx('input-wrapper-number')}>
            <Amount control={control} name='total_amount_from' />
            <span className={cx('amount-range-separator')}>~</span>
            <Amount control={control} name='total_amount_to' />
          </div>
        </FormContainer>
        {/* Row 2-4 attached invoice input */}
        <FormContainer className={cx('field-width-240')}>
          <div className={cx('select-wrapper')}>
            <label className={cx('label-text')}>{t('attached_invoice')}</label>
            <InvoiceMFFileStatusesSelect
              control={control}
              name='mf_file'
              formContext={form}
            />
          </div>
        </FormContainer>
        {/* Row 2-5 without agent input */}
        <FormContainer className={cx('field-width-106')}>
          <div className={cx('checkbox-wrapper')}>
            <FormCheckBox
              control={control}
              name='without_agent'
              labelProps={customProps?.labelProps?.withoutAgent}
            />
          </div>
        </FormContainer>
      </Flex>

      {/* Row 3 */}
      <Flex gap={8} className={cx('form-row')}>
        {/* Row 3-1 created date input */}
        <FormContainer className={cx('field-width-fill')}>
          <DateRangeSelect
            control={control}
            name='created_date'
            labelProps={customProps?.labelProps?.createdDate}
          />
        </FormContainer>
        {/* Row 3-2 submitted date input */}
        <FormContainer className={cx('field-width-fill')}>
          <DateRangeSelect
            control={control}
            name='submitted_date'
            labelProps={customProps?.labelProps?.submittedDate}
          />
        </FormContainer>
        {/* Row 3-3 approved date input */}
        <FormContainer className={cx('field-width-fill')}>
          <DateRangeSelect
            control={control}
            name='approved_date'
            labelProps={customProps?.labelProps?.approvedDate}
          />
        </FormContainer>
        {/* Row 3-4 book date input */}
        <FormContainer className={cx('field-width-fill')}>
          <DateRangeSelect
            control={control}
            name='book_date'
            labelProps={customProps?.labelProps?.bookDate}
          />
        </FormContainer>
        {/* Row 3-5 due date input */}
        <FormContainer className={cx('field-width-fill')}>
          <DateRangeSelect
            control={control}
            name='due_date'
            labelProps={customProps?.labelProps?.dueDate}
          />
        </FormContainer>
      </Flex>

      {/* Action Buttons */}
      <div className={cx('action-buttons')}>
        <ButtonV2
          size='sm'
          isTertiary
          onClick={onReset}
          className={cx('reset-button')}
        >
          {t('reset')}
        </ButtonV2>
        <ButtonV2
          size='sm'
          isSecondary
          type='submit'
          className={cx('search-button')}
          isLoading={isLoading}
          isDisabled={isLoading}
        >
          {t('search')}
        </ButtonV2>
      </div>
    </Form>
  );
};
